import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import SectionBadge from "@/components/shared/Title";
import ReviewCard from "./ReviewCard";
import SectionLeft from "./SectionLeft";

import { LuMessageCircle } from "react-icons/lu";

import MotionDiv from "@/components/animations/MotionDiv";
import { fadeUp, stagger } from "@/animations/variants";
import { AnimatePresence, motion } from "framer-motion";

import { useReviews } from "@/hooks/useReviews";

const ReviewsSection = () => {
  const { t, i18n } = useTranslation();
  const isAR = i18n.language?.toLowerCase().startsWith("ar");

  const { reviews, loading, error } = useReviews(isAR);

  const [index, setIndex] = useState(0);

  const total = reviews.length;

  const visibleReviews = useMemo(() => {
    if (!total) return [];

    return [0, 1, 2, 3]
      .map((offset) => reviews[(index + offset) % total])
      .filter(Boolean);
  }, [reviews, index, total]);

  const progress = total > 0 ? ((index + 1) / total) * 100 : 0;

  const next = () => {
    if (!total) return;
    setIndex((prev) => (prev + 1) % total);
  };

  const prev = () => {
    if (!total) return;
    setIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
  };

  useEffect(() => {
    if (!total) return;

    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5000);

    return () => clearInterval(interval);
  }, [total]);

  useEffect(() => {
    if (index >= total && total > 0) {
      setIndex(0);
    }
  }, [index, total]);

  return (
    <section id="testimonials" className="py-12 sm:py-16 lg:py-20">
      <div className="container mx-auto px-4">
        {/* BADGE */}
        <MotionDiv variants={fadeUp} className="flex justify-center">
          <SectionBadge
            text={t("reviewSection.badge")}
            icon={<LuMessageCircle />}
          />
        </MotionDiv>

        {/* TITLE */}
        <MotionDiv variants={fadeUp}>
          <h2 className="text-primary mt-5 text-center text-2xl leading-snug font-bold sm:mt-6 sm:text-3xl lg:text-4xl">
            {t("reviewSection.title")}
          </h2>
        </MotionDiv>

        {/* SUBTITLE */}
        <MotionDiv variants={fadeUp}>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-7 text-gray-500 sm:text-base">
            {t("reviewSection.subtitle")}
          </p>
        </MotionDiv>

        {/* LOADING */}
        {loading && (
          <p className="mt-10 text-center text-sm text-gray-500">
            {isAR ? "جاري تحميل آراء العملاء..." : "Loading reviews..."}
          </p>
        )}

        {/* ERROR */}
        {!loading && error && (
          <p className="mt-10 text-center text-sm text-red-500">
            {isAR ? "حدث خطأ أثناء تحميل آراء العملاء" : error}
          </p>
        )}

        {/* EMPTY */}
        {!loading && !error && total === 0 && (
          <p className="mt-10 text-center text-sm text-gray-500">
            {isAR ? "لا توجد آراء حالياً" : "No reviews available"}
          </p>
        )}

        {/* CONTENT */}
        {!loading && !error && total > 0 && (
          <div className="mt-12 grid gap-10 lg:mt-16 lg:grid-cols-2 lg:items-center">
            {/* LEFT */}
            <MotionDiv variants={fadeUp}>
              <SectionLeft
                title={t("reviewSection.desc")}
                onPrev={prev}
                onNext={next}
                progress={progress}
              />
            </MotionDiv>

            {/* RIGHT CARDS */}
            <MotionDiv
              variants={stagger}
              className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6"
            >
              <AnimatePresence mode="popLayout">
                {visibleReviews.map((item, i) => (
                  <motion.div
                    key={`${item.id}-${index}-${i}`}
                    layout
                    initial={{ opacity: 0, y: 24, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -16, scale: 0.96 }}
                    transition={{
                      duration: 0.35,
                      ease: "easeOut",
                      delay: i * 0.06,
                    }}
                    className="h-full"
                  >
                    <ReviewCard
                      text={item.text}
                      name={item.name}
                      role={item.role}
                      avatar={item.avatar}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </MotionDiv>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;